/**
 * BaseBanner - Reusable alert banner component.
 *
 * Consolidates common patterns from:
 * - AnomalyBanner.tsx
 * - GammaRiskBanner.tsx
 * - EdgeWarningBanner.tsx
 * - DeploymentBanner.tsx
 * - ImpersonationBanner.tsx
 *
 * Features:
 * - Severity-based styling (critical/warning/info)
 * - Expandable details for multiple items
 * - Dismissable with optional callback
 * - Consistent accessibility (role="alert", aria-live)
 * - Customizable icon and actions
 *
 * Usage:
 *   import { BaseBanner } from '@/components/ui/BaseBanner';
 *
 *   <BaseBanner
 *     severity="critical"
 *     title="Short Gamma Risk"
 *     items={[
 *       { id: '1', content: <span>NVDA 240315C200: 2 DTE</span> },
 *       { id: '2', content: <span>AAPL 240308P150: 1 DTE</span> },
 *     ]}
 *     icon={Flame}
 *     onDismiss={() => setDismissed(true)}
 *     maxVisible={3}
 *   />
 */

import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  ChevronDown,
  ChevronUp,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/Button";

export type BannerSeverity = "critical" | "warning" | "info";

export interface BannerItem {
  /** Unique identifier for the item */
  id: string;
  /** Content to display (can be string or JSX) */
  content: ReactNode;
  /** Optional severity override for this specific item */
  severity?: BannerSeverity;
}

export interface BaseBannerProps {
  /** Banner severity determines styling */
  severity: BannerSeverity;
  /** Main title text */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** List of items to display (first is always shown, rest are expandable) */
  items?: BannerItem[];
  /** Custom icon (defaults based on severity) */
  icon?: LucideIcon;
  /** Whether the banner can be expanded to show more items */
  expandable?: boolean;
  /** Whether to show a dismiss button */
  dismissable?: boolean;
  /** Maximum items to show before requiring expansion */
  maxVisible?: number;
  /** Called when dismiss button is clicked */
  onDismiss?: () => void;
  /** Custom action buttons to show in the header */
  actions?: ReactNode;
  /** Footer content (shown after items) */
  footer?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Children are rendered after subtitle, before items */
  children?: ReactNode;
}

/**
 * Severity-based styling configuration.
 */
const severityConfig: Record<
  BannerSeverity,
  {
    bg: string;
    border: string;
    titleText: string;
    subtitleText: string;
    itemText: string;
    icon: LucideIcon;
    iconClass: string;
  }
> = {
  critical: {
    bg: "bg-negative-tint",
    border: "border-negative-border",
    titleText: "text-negative",
    subtitleText: "text-negative",
    itemText: "text-foreground-muted",
    icon: AlertCircle,
    iconClass: "text-negative",
  },
  warning: {
    bg: "bg-warning-tint",
    border: "border-warning-border",
    titleText: "text-warning",
    subtitleText: "text-warning",
    itemText: "text-foreground-muted",
    icon: AlertTriangle,
    iconClass: "text-warning",
  },
  info: {
    bg: "bg-info-tint",
    border: "border-info-border",
    titleText: "text-info",
    subtitleText: "text-info",
    itemText: "text-foreground-muted",
    icon: Info,
    iconClass: "text-info",
  },
};

export function BaseBanner({
  severity,
  title,
  subtitle,
  items = [],
  icon,
  expandable = true,
  dismissable = false,
  maxVisible = 1,
  onDismiss,
  actions,
  footer,
  className,
  children,
}: BaseBannerProps) {
  const [expanded, setExpanded] = useState(false);
  const config = severityConfig[severity];
  const Icon = icon ?? config.icon;

  const visibleItems = items.slice(0, maxVisible);
  const hiddenItems = items.slice(maxVisible);
  const hasMoreItems = hiddenItems.length > 0 && expandable;

  return (
    <div
      className={cn(
        "w-full rounded-lg border p-4 mb-4",
        config.bg,
        config.border,
        className,
      )}
      role="alert"
      aria-live={severity === "critical" ? "assertive" : "polite"}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Icon
            className={cn(
              "size-5 mt-0.5 flex-shrink-0",
              config.iconClass,
              severity === "critical" && "animate-breathe",
            )}
            aria-hidden="true"
          />
          <div className="flex-1">
            <h3 className={cn("font-semibold", config.titleText)}>{title}</h3>

            {subtitle && (
              <p className={cn("text-sm mt-0.5", config.subtitleText)}>
                {subtitle}
                {hasMoreItems && !expanded && (
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="ml-2 inline-flex items-center gap-1 underline hover:no-underline"
                  >
                    +{hiddenItems.length} more{" "}
                    <ChevronDown className="size-3" />
                  </button>
                )}
              </p>
            )}

            {children}

            {/* Visible items */}
            {visibleItems.length > 0 && (
              <div className="mt-2 space-y-1">
                {visibleItems.map((item) => (
                  <div
                    key={item.id}
                    className={cn(
                      "text-sm",
                      item.severity
                        ? severityConfig[item.severity].itemText
                        : config.itemText,
                    )}
                  >
                    {item.content}
                  </div>
                ))}
              </div>
            )}

            {/* Expand/collapse toggle (when no subtitle) */}
            {!subtitle && hasMoreItems && (
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className={cn(
                  "mt-2 inline-flex items-center gap-1 text-sm underline hover:no-underline",
                  config.subtitleText,
                )}
              >
                {expanded ? (
                  <>
                    Hide details <ChevronUp className="size-3" />
                  </>
                ) : (
                  <>
                    +{hiddenItems.length} more{" "}
                    <ChevronDown className="size-3" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {actions}
          {dismissable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDismiss}
              className="size-8"
              aria-label="Dismiss alert"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded items */}
      {expanded && hiddenItems.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-current/10 pt-4">
          {hiddenItems.map((item) => (
            <div
              key={item.id}
              className={cn(
                "text-sm",
                item.severity
                  ? severityConfig[item.severity].itemText
                  : config.itemText,
              )}
            >
              {item.content}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className={cn(
              "inline-flex items-center gap-1 text-sm underline hover:no-underline",
              config.subtitleText,
            )}
          >
            Hide details <ChevronUp className="size-3" />
          </button>
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div className="mt-3 pt-3 border-t border-current/10">{footer}</div>
      )}
    </div>
  );
}

/**
 * Compact banner for inline alerts (no expand, minimal padding).
 */
export function CompactBanner({
  severity,
  message,
  icon,
  dismissable = false,
  onDismiss,
  className,
}: {
  severity: BannerSeverity;
  message: ReactNode;
  icon?: LucideIcon;
  dismissable?: boolean;
  onDismiss?: () => void;
  className?: string;
}) {
  const config = severityConfig[severity];
  const Icon = icon ?? config.icon;

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-md text-sm",
        config.bg,
        config.border,
        "border",
        className,
      )}
      role="alert"
    >
      <Icon
        className={cn("size-4 flex-shrink-0", config.iconClass)}
        aria-hidden="true"
      />
      <span className={cn("flex-1", config.subtitleText)}>{message}</span>
      {dismissable && (
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 hover:opacity-70"
          aria-label="Dismiss"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}

export { severityConfig };
